<template>
  <div v-intersect @intersect:enter="onShowPagination">
    <CommonShowMore
      v-if="hasShowMore"
      class="pagination-load"
      :fetching="fetching"
      :limit="limit"
      :offset="offset"
      :count="totalItems"
      @on-show-more="handleShowMore"
    />

    <ul v-if="showPagination" class="pagination">
      <li
        class="pagination__item pagination__arrow pagination__arrow--left"
        :class="{
          'pagination__arrow--disabled': !hasPrev,
        }"
        @click.prevent="hasPrev ? onChangePage(prevPage) : () => false"
      >
        <a v-if="hasPrev" :href="getPageLink(prevPage)" @click.prevent>
          <span class="icon-chevron-left-solid" />
        </a>
        <span v-else class="icon-chevron-left-solid" />
      </li>

      <CommonPaginationItem
        v-if="rangeStart > 1"
        :page="1"
        :link="getPageLink(1)"
        :is-active="isActivePage(1)"
        @click="onChangePage"
      />

      <li v-if="rangeStart > 2" class="pagination__splitter">
        ...
      </li>

      <CommonPaginationItem
        v-for="page in pages"
        :key="page"
        :page="page"
        :link="getPageLink(page)"
        :is-active="isActivePage(page)"
        @click="onChangePage"
      />

      <li v-if="rangeEnd < totalPages - 1" class="pagination__splitter">
        ...
      </li>

      <CommonPaginationItem
        v-if="rangeEnd < totalPages"
        :page="totalPages"
        :link="getPageLink(totalPages)"
        :is-active="isActivePage(totalPages)"
        @click="onChangePage"
      />

      <li
        class="pagination__item pagination__arrow pagination__arrow--right"
        :class="{
          'pagination__arrow--disabled': !hasNext,
        }"
        @click.prevent="hasNext ? onChangePage(nextPage) : () => false"
      >
        <a v-if="hasNext" :href="getPageLink(nextPage)" @click.prevent>
          <span class="icon-chevron-right-solid" />
        </a>
        <span v-else class="icon-chevron-right-solid" />
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { CATALOG_LIMIT } from '@/constants/common'
import type { PageOptions } from '~/types/page'
import type { QueryParams } from '~/types/query'

type Props = {
  fetching?: boolean
  totalItems?: number
  showMore?: boolean
  // items count per page
  limit?: number
}

type Emits = {
  (e: 'on-show-more', options: PageOptions): void
  (e: 'on-change-page', options: PageOptions): void
}

const props = withDefaults(defineProps<Props>(), {
  fetching: false,
  totalItems: 0,
  showMore: false,
  limit: CATALOG_LIMIT
})

const emits = defineEmits<Emits>()

const { $device } = useNuxtApp()
const route = useRoute()
const commonMetricsStore = useCommonMetricsStore()

const pageStartMore = shallowRef(0)

// Computed START
const innerPageRange = computed(() => {
  return ($device.isMobile) ? 1 : 2
})
const currentPage = computed(() => {
  const page = unref(route).query?.page

  return page ? +page : 1
})
const offset = computed(() => {
  return (currentPage.value - 1) * props.limit
})
const totalPages = computed(() => {
  return Math.ceil(props.totalItems / props.limit)
})
const difference = computed(() => {
  const activeMore: boolean = pageStartMore.value > 0

  return activeMore
    ? currentPage.value - pageStartMore.value >= innerPageRange.value
    : activeMore
})
const rangeStart = computed(() => {
  const start: number = difference.value
    ? currentPage.value - (innerPageRange.value * 2 - 1)
    : currentPage.value - innerPageRange.value

  return Math.max(1, start)
})
const rangeEnd = computed(() => {
  const end: number = difference.value
    ? currentPage.value + 1
    : currentPage.value + innerPageRange.value

  return Math.min(totalPages.value, end)
})
const pages = computed(() => {
  return Array.from(
    { length: rangeEnd.value - rangeStart.value + 1 },
    (_, i) => rangeStart.value + i
  )
})
const nextPage = computed(() => {
  return currentPage.value + 1
})
const prevPage = computed(() => {
  return currentPage.value - 1
})
const hasPrev = computed(() => {
  return currentPage.value > 1
})
const hasNext = computed(() => {
  return currentPage.value < totalPages.value
})
const showPagination = computed(() => {
  return props.totalItems > props.limit
})
const hasShowMore = computed(() => {
  return props.showMore && hasNext.value
})
// Computed END

// Methods START
const getPageLink = (page: number) => {
  const { path, query } = unref(route)
  const newQuery: QueryParams = {
    ...query,
    page: page.toString()
  }

  if (page === 1) {
    delete newQuery.page
  }

  const queryString = buildFilterQueryString(newQuery)

  return queryString.length === 0
    ? path
    : `${path}?${queryString}`
}

const handleShowMore = (offset: number) => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[pagination-show-more]',
    event_type: 'click',
    event_content: offset
  })

  const page = currentPage.value + 1

  if (pageStartMore.value === 0) {
    pageStartMore.value = currentPage.value
  }

  navigateTo({
    query: {
      ...unref(route).query,
      page
    }
  })
  emits('on-show-more', { offset, page, limit: props.limit })
}

const onShowPagination = () => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[pagination]',
    event_type: 'view',
    event_content: offset
  })
}

const onChangePage = async (page: number) => {
  pageStartMore.value = 0

  navigateTo({
    query: {
      ...unref(route).query,
      page
    }
  })

  emits('on-change-page', {
    page,
    offset: (page - 1) * props.limit,
    limit: props.limit
  })

  commonMetricsStore.addMetricEvent({
    element: 'ms[pagination-page]',
    event_type: 'click',
    event_content: page
  })
}

const isActivePage = (page: number) => {
  return !pageStartMore.value
    ? currentPage.value === page
    : page >= pageStartMore.value && page <= currentPage.value
}
// Methods END

// onMounted(() => {
//   innerPageRange.value = getInnerPageRange.value
// })
</script>

<style lang="scss">
.load {
  transition: rotate .2s;
  transform: rotate(0deg)

  &-active{
    animation: spin .6s infinite;
  }
}

@keyframes spin {
  100% {
    transform:rotate(360deg)
  }
}

.pagination-load {
  margin-bottom: 2rem;
  @media only screen and (max-width: 740px) {
    margin-bottom: 1.6rem;
  }
}

.pagination {
  padding: 0;
  display: flex;
  list-style: none;
  justify-content: center;
  margin-bottom: 0;

  a {
    color: inherit;
    text-decoration: none;
  }

  &__item,
  &__splitter {
    min-height: 3.6rem;
    min-width: 3.6rem;
    background-color: transparent;
    border: 1px solid #E7E7E7;
    box-sizing: border-box;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 .7rem;
    font-size: 1.2rem;
    font-weight: 500;

    @media only screen and (max-width: 740px) {
      min-width: 3rem;
      min-height: 3rem;
      margin-left: 0.4rem;
      margin-right: 0.4rem;
    }

    &--active {
      border: 2px solid $primary-color;
    }
  }

  &__link {
    color: inherit;
    text-decoration: none;
  }

  &__splitter {
    @media only screen and (max-width: 740px) {
      border: none;
      min-width: .6rem;
      min-height: 3rem;
      margin-left: 0.2rem;
      margin-right: 0.2rem;
      font-size: .8rem;
    }
  }

  &__item {
    cursor: pointer;
    @media only screen and (min-width: 1024px) {
      &:hover {
        background-color: #F8F8F8;
      }
    }
  }

  &__arrow {
    font-size: 1rem;
  }
}
</style>
